import { Injectable } from '@angular/core';
import { CalendarService } from '../../services/calendar/calendar.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CourseActions, CourseApiActions } from './course.actions';
import { catchError, map, of, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { ObjectUtilsService } from '../../services/core/object-utils.service';
import { Course } from './model/course.model';

@Injectable()
export class CourseEffects {
    constructor(
        private actions$: Actions,
        private calendarService: CalendarService,
        private router: Router,
        private objectUtilsService: ObjectUtilsService
    ) {}

    loadCourses$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CourseApiActions.loadCourses),
            switchMap(() =>
                this.calendarService.loadCourses().pipe(
                    map((courses) =>
                        courses.map((course) => {
                            course.instructor = this.objectUtilsService.buildName(course.instructor);
                            return course;
                        })
                    ),
                    map((courses) => CourseActions.loadCoursesSuccess({ courses })),
                    catchError((error) => of(CourseActions.loadCoursesFailure({ error })))
                )
            )
        );
    });

    createCourse$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CourseApiActions.createCourse),
            switchMap(({ course }) =>
                this.calendarService.createCourse(course).pipe(
                    map((course) => {
                        void this.router.navigate(['/calendar/admin']);
                        return CourseActions.createCourseSuccess({ course });
                    }),
                    catchError((error) => of(CourseActions.createCourseFailure({ error })))
                )
            )
        );
    });

    updateCourse$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CourseApiActions.updateCourse),
            switchMap(({ course }) =>
                this.calendarService.updateCourse(course).pipe(
                    map((course) => {
                        void this.router.navigate(['/calendar/admin']);
                        return CourseActions.updateCourseSuccess({ course });
                    }),
                    catchError((error) => of(CourseActions.updateCourseFailure({ error })))
                )
            )
        );
    });

    activateCourse$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CourseApiActions.activateCourse),
            switchMap(({ course }) =>
                this.calendarService.activateCourse(course).pipe(
                    map(() => CourseActions.changeActiveStatusSuccess({ course: course })),
                    catchError((error) => of(CourseActions.changeActiveStatusFailure({ error })))
                )
            )
        );
    });

    deactivateCourse$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CourseApiActions.deactivateCourse),
            switchMap(({ course }) =>
                this.calendarService.deactivateCourse(course).pipe(
                    map(() => {
                        const deactivated: Course = {
                            ...course,
                            active: false
                        };
                        return CourseActions.updateCourseSuccess({ course: deactivated });
                    }),
                    catchError((error) => of(CourseActions.updateCourseFailure({ error })))
                )
            )
        );
    });

    deleteCourse$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CourseApiActions.deleteCourse),
            switchMap(({ course }) =>
                this.calendarService.deleteCourse(course).pipe(
                    map(() => CourseActions.deleteCourseSuccess({ course: course })),
                    catchError((error) => of(CourseActions.deleteCourseFailure({ error })))
                )
            )
        );
    });
}
